/**
 * Bảng giá: 3 vùng × các HÀNG cân + giá mỗi bước trên hàng cuối + cân tối đa.
 *
 * Mỗi hàng là "dưới X kg" (lt), "đúng X kg" (eq) hoặc "đến X kg, gồm X" (le). Ví dụ bảng
 * BEST: <1kg · =1kg · 1–2kg (dưới 2) · =2kg · … Từ 10/2026 MỖI BẢNG tự có hàng của mình
 * (PriceTable, lưu kèm dữ liệu giá); bảng cũ chưa có thì dùng khung chung (D24, toàn 'le').
 * So hai bảng khác hàng (dưới giá nhập P4, trần hoa hồng D25) bằng cách ghép mốc của cả hai
 * thành một khung chung rồi so từng ô — unionGrid() / compareTables() / minMarginTables().
 *
 * Nhờ dùng chung khung:
 *  - "dưới giá nhập" là so từng ô (P4) — compareBelow()
 *  - trần hoa hồng CTV = chênh lệch nhỏ nhất giữa hai bảng (D25) — minMargin()
 *  - kiện nặng tính theo bước, vượt cân tối đa thì không cho đặt đơn (P5)
 *
 * Cân nặng xử lý bằng GRAM nguyên để không vướng sai số số thực (0,1 + 0,2 …).
 * Xem docs/KE_HOACH_TAI_CAU_TRUC.md §2.3.
 */
import { ROUTE_LABELS, SHIPPING_ROUTES, type ShippingRoute } from './routes.js'

/** Loại hàng: dưới X (lt) · đúng X (eq) · đến X, gồm X (le). */
export type RowKind = 'lt' | 'eq' | 'le'
export const ROW_KINDS: readonly RowKind[] = ['lt', 'eq', 'le'] as const

export interface PriceGrid {
  version: number
  /** Mốc cân (kg) của từng hàng, không giảm, > 0. */
  breakpointsKg: number[]
  /** Loại của từng hàng (cùng độ dài breakpointsKg); thiếu = mọi hàng 'le' (khung cũ). */
  kinds?: RowKind[]
  /** Bước tính thêm trên mốc cuối (kg), > 0. */
  stepKg: number
  /** Cân tối đa (kg) — vượt thì không cho đặt đơn. */
  maxWeightKg: number
}

export interface ZonePrices {
  /** Giá từng mốc, cùng độ dài với breakpointsKg. */
  prices: number[]
  /** Giá cộng thêm cho mỗi bước trên mốc cuối. */
  stepPrice: number
}

export type PriceListData = Record<ShippingRoute, ZonePrices>

/** Một bảng giá tự đủ: hàng cân + giá. */
export interface PriceTable {
  grid: PriceGrid
  data: PriceListData
}

/** Dữ liệu phiên bản bảng giá lưu trong DB: bản mới kèm `grid` riêng; bản cũ chỉ có giá theo khung chung. */
export type StoredPriceData = PriceListData & { grid?: PriceGrid }

export function kindAt(grid: PriceGrid, i: number): RowKind {
  return grid.kinds?.[i] ?? 'le'
}

/**
 * Bảng lưu trước khi có cột Liên tỉnh (3 vùng) → Liên tỉnh lấy giá Nội miền, để giá đơn
 * không đổi cho tới khi chủ bảng sửa (docs/KE_HOACH_COT_LIEN_TINH.md, Q3).
 */
export function withLienTinh<T extends Partial<Record<ShippingRoute, ZonePrices>>>(data: T): T & PriceListData {
  if (data.lien_tinh || !data.noi_mien) return data as T & PriceListData
  return { ...data, lien_tinh: { prices: [...data.noi_mien.prices], stepPrice: data.noi_mien.stepPrice } } as T & PriceListData
}

/** Tách dữ liệu lưu trong DB → bảng giá; bản cũ dùng `legacyGrid` (khung chung theo gridVersion). */
export function tableFromStored(stored: unknown, legacyGrid: PriceGrid | null): PriceTable {
  const raw = withLienTinh(stored as StoredPriceData)
  const data = {} as PriceListData
  for (const z of SHIPPING_ROUTES) data[z] = raw[z]
  if (raw.grid) return { grid: raw.grid, data }
  if (!legacyGrid) throw new Error('Bảng giá cũ thiếu khung chung')
  return { grid: legacyGrid, data }
}

/** Dữ liệu lưu cho một bảng giá mới — giá + hàng cân riêng của bảng. */
export function storedFromTable(t: PriceTable): StoredPriceData {
  const { version: _v, ...grid } = t.grid
  return { ...t.data, grid: { version: 0, ...grid } }
}

export type PriceGridErrorCode = 'invalid_weight' | 'over_max_weight'

export class PriceGridError extends Error {
  constructor(
    public readonly code: PriceGridErrorCode,
    message: string,
  ) {
    super(message)
    this.name = 'PriceGridError'
  }
}

/** Ô của khung: một mốc cân của một vùng, hoặc một cân nặng cụ thể trong vùng tính theo bước. */
export interface CellRef {
  zone: ShippingRoute
  /** Chỉ số mốc, hoặc 'step' khi nằm trên mốc cuối. */
  cell: number | 'step'
  /** Cân nặng đại diện (gram) — mốc, hoặc cân nặng cụ thể trong vùng bước. */
  weightGram: number
  label: string
}

const toGram = (kg: number): number => Math.round(kg * 1000)

/** Bước tính thêm nhỏ nhất — bước quá nhỏ làm khung ghép hai bảng (unionGrid) phình hàng chục nghìn hàng. */
export const MIN_STEP_KG = 0.1
const MIN_STEP_GRAM = toGram(MIN_STEP_KG)

function fmtKg(gram: number): string {
  return `${String(gram / 1000).replace('.', ',')}kg`
}

/** Nhãn một hàng: "≤ 2kg", "< 2kg", "= 2kg". */
export function rowLabel(grid: PriceGrid, i: number): string {
  const kg = fmtKg(toGram(grid.breakpointsKg[i]!))
  const k = kindAt(grid, i)
  return k === 'lt' ? `< ${kg}` : k === 'eq' ? `= ${kg}` : `≤ ${kg}`
}

function cellLabel(grid: PriceGrid, zone: ShippingRoute, cell: number | 'step', weightGram: number): string {
  if (cell === 'step') return `${ROUTE_LABELS[zone]} ${fmtKg(weightGram)}`
  return `${ROUTE_LABELS[zone]} ${rowLabel(grid, cell)}`
}

// ─── Kiểm tra dữ liệu ────────────────────────────────────────────

export function validateGrid(grid: PriceGrid): string[] {
  const errors: string[] = []
  const bp = grid.breakpointsKg.map(toGram)
  if (bp.length === 0) errors.push('Bảng phải có ít nhất một hàng cân')
  if (grid.kinds && grid.kinds.length !== bp.length) errors.push('Số loại hàng không khớp số hàng')
  if (grid.kinds?.some((k) => !ROW_KINDS.includes(k))) errors.push('Loại hàng không hợp lệ')
  bp.forEach((g, i) => {
    const k = kindAt(grid, i)
    if (!(g > 0)) errors.push(`Hàng thứ ${i + 1}: cân phải lớn hơn 0`)
    if (i > 0) {
      const pk = kindAt(grid, i - 1)
      // Cùng mốc chỉ được "dưới X" rồi "đúng X"; còn lại mốc phải tăng dần.
      if (g < bp[i - 1]! || (g === bp[i - 1]! && !(pk === 'lt' && k === 'eq'))) errors.push(`Hàng thứ ${i + 1} (${fmtKg(g)}) phải nặng hơn hàng trước`)
    }
    // "Đúng X" chỉ phủ đúng điểm X → phải có "dưới X" ngay trước, nếu không khoảng trước X bị hở.
    if (k === 'eq' && !(i > 0 && kindAt(grid, i - 1) === 'lt' && bp[i - 1] === g)) {
      errors.push(`Hàng thứ ${i + 1} "= ${fmtKg(g)}" phải đứng ngay sau hàng "dưới ${fmtKg(g)}"`)
    }
  })
  if (bp.length && kindAt(grid, bp.length - 1) === 'lt') errors.push('Hàng cuối phải là "đúng X kg" hoặc "đến X kg" (không để hở đúng mốc cuối)')
  if (!(toGram(grid.stepKg) >= MIN_STEP_GRAM)) errors.push(`Bước tính thêm tối thiểu ${fmtKg(MIN_STEP_GRAM)}`)
  const last = bp[bp.length - 1]
  if (last !== undefined && toGram(grid.maxWeightKg) < last) {
    errors.push('Cân tối đa không được nhỏ hơn mốc cuối')
  }
  return errors
}

/** Giá phải là số nguyên VND ≥ 0, đủ mọi vùng, không giảm theo mốc cân. */
export function validatePriceListData(grid: PriceGrid, data: PriceListData): string[] {
  const errors: string[] = []
  for (const zone of SHIPPING_ROUTES) {
    const z = data[zone]
    const name = ROUTE_LABELS[zone]
    if (!z) {
      errors.push(`Thiếu vùng ${name}`)
      continue
    }
    if (z.prices.length !== grid.breakpointsKg.length) {
      errors.push(`${name}: cần ${grid.breakpointsKg.length} giá, có ${z.prices.length}`)
      continue
    }
    z.prices.forEach((p, i) => {
      if (!Number.isSafeInteger(p) || p < 0) errors.push(`${name} hàng ${rowLabel(grid, i)}: giá phải là số nguyên ≥ 0`)
      if (i > 0 && p < z.prices[i - 1]!) errors.push(`${name} hàng ${rowLabel(grid, i)}: giá không được thấp hơn hàng nhẹ hơn`)
    })
    if (!Number.isSafeInteger(z.stepPrice) || z.stepPrice < 0) {
      errors.push(`${name}: giá mỗi bước phải là số nguyên ≥ 0`)
    }
  }
  return errors
}

// ─── Tra giá ─────────────────────────────────────────────────────

/** Số bước trên mốc cuối của một kiện (0 nếu nằm trong các mốc). */
function stepsAbove(grid: PriceGrid, weightGram: number): number {
  const last = toGram(grid.breakpointsKg[grid.breakpointsKg.length - 1]!)
  if (weightGram <= last) return 0
  return Math.ceil((weightGram - last) / toGram(grid.stepKg))
}

/** Giá một kiện theo vùng và cân nặng (gram). Ném PriceGridError nếu cân không hợp lệ / vượt tối đa (P5). */
export function priceFor(grid: PriceGrid, data: PriceListData, zone: ShippingRoute, weightGram: number): number {
  if (!Number.isSafeInteger(weightGram) || weightGram <= 0) {
    throw new PriceGridError('invalid_weight', `Cân nặng không hợp lệ: ${weightGram}g`)
  }
  if (weightGram > toGram(grid.maxWeightKg)) {
    throw new PriceGridError(
      'over_max_weight',
      `Kiện ${fmtKg(weightGram)} vượt cân tối đa ${fmtKg(toGram(grid.maxWeightKg))}`,
    )
  }
  const z = data[zone]
  const idx = grid.breakpointsKg.findIndex((kg, i) => {
    const g = toGram(kg)
    const k = kindAt(grid, i)
    return k === 'lt' ? weightGram < g : k === 'eq' ? weightGram === g : weightGram <= g
  })
  if (idx >= 0) return z.prices[idx]!
  return z.prices[z.prices.length - 1]! + stepsAbove(grid, weightGram) * z.stepPrice
}

/** Tổng giá nhiều kiện của một đơn — mỗi kiện tính riêng. */
export function priceForParcels(
  grid: PriceGrid,
  data: PriceListData,
  zone: ShippingRoute,
  weightsGram: readonly number[],
): number {
  if (weightsGram.length === 0) throw new PriceGridError('invalid_weight', 'Đơn phải có ít nhất một kiện')
  return weightsGram.reduce((s, w) => s + priceFor(grid, data, zone, w), 0)
}

/** BEST: khối lượng quy đổi (kg) = Dài × Rộng × Cao (cm) / 6000 (bảng giá công khai best-inc.vn). */
export const VOLUMETRIC_DIVISOR = 6000

/** BEST: cân thực ≤ 3kg VÀ cân quy đổi ≤ 5kg → tính theo cân thực (bảng quy tắc cân BEST, 03/10). */
export const ACTUAL_ONLY_MAX_ACTUAL_GRAM = 3_000
export const ACTUAL_ONLY_MAX_VOLUMETRIC_GRAM = 5_000

/**
 * Quy đổi cân theo thể tích — Super Admin bật / tắt và đổi công thức ở Cài đặt nền tảng (PlatformSetting
 * dòng NPP mặc định, volumetricRule() ở packages/finance/src/settings.ts). Tắt: cân tính cước = cân thực,
 * kích thước thùng không ảnh hưởng giá lẫn tài khoản hãng.
 */
export interface VolumetricRule {
  enabled: boolean
  /** Hệ số chia: D × R × C (cm) / divisor = kg */
  divisor: number
  /** Cân thực ≤ số này VÀ quy đổi ≤ actualOnlyMaxVolumetricGram → tính theo cân thực */
  actualOnlyMaxActualGram: number
  actualOnlyMaxVolumetricGram: number
}

export const DEFAULT_VOLUMETRIC_RULE: VolumetricRule = {
  enabled: true,
  divisor: VOLUMETRIC_DIVISOR,
  actualOnlyMaxActualGram: ACTUAL_ONLY_MAX_ACTUAL_GRAM,
  actualOnlyMaxVolumetricGram: ACTUAL_ONLY_MAX_VOLUMETRIC_GRAM,
}

/** Giới hạn Super Admin được đặt cho hệ số chia (admin-api kiểm, trang Cài đặt nền tảng hiện). */
export const VOLUMETRIC_DIVISOR_MIN = 1_000
export const VOLUMETRIC_DIVISOR_MAX = 20_000

export interface ParcelDims {
  lengthMm: number
  widthMm: number
  heightMm: number
}

/** Khối lượng quy đổi theo gram, làm tròn lên. Kích thước theo mm như API BEST: mm³ / 6000 = gram. */
export function volumetricWeightGram(d: ParcelDims, rule: VolumetricRule = DEFAULT_VOLUMETRIC_RULE): number {
  for (const v of [d.lengthMm, d.widthMm, d.heightMm]) {
    if (!Number.isSafeInteger(v) || v <= 0) throw new PriceGridError('invalid_weight', `Kích thước không hợp lệ: ${v}mm`)
  }
  return Math.ceil((d.lengthMm * d.widthMm * d.heightMm) / rule.divisor)
}

/**
 * Cân tính cước theo quy tắc BEST: cân thực ≤ 3kg và cân quy đổi ≤ 5kg → cân thực;
 * cân thực > 3kg hoặc cân quy đổi > 5kg → lấy giá trị lớn hơn. Không có cân quy đổi / quy đổi tắt → cân thực.
 */
export function chargeableFromParts(actualGram: number, volumetricGram?: number | null, rule: VolumetricRule = DEFAULT_VOLUMETRIC_RULE): number {
  if (!Number.isSafeInteger(actualGram) || actualGram <= 0) {
    throw new PriceGridError('invalid_weight', `Cân nặng không hợp lệ: ${actualGram}g`)
  }
  if (!rule.enabled || !volumetricGram || volumetricGram <= 0) return actualGram
  if (actualGram <= rule.actualOnlyMaxActualGram && volumetricGram <= rule.actualOnlyMaxVolumetricGram) return actualGram
  return Math.max(actualGram, volumetricGram)
}

/** Cân tính cước của một kiện (quy tắc BEST — chargeableFromParts); không có kích thước thì là cân thực. */
export function chargeableWeightGram(actualGram: number, dims?: ParcelDims | null, rule: VolumetricRule = DEFAULT_VOLUMETRIC_RULE): number {
  return chargeableFromParts(actualGram, dims && rule.enabled ? volumetricWeightGram(dims, rule) : null, rule)
}

/** Cân nhỏ nhất / lớn nhất của một kiện gửi hãng (gram) — createOrderSchema shipment.weightGram. */
export const MIN_PARCEL_GRAM = 50
export const MAX_PARCEL_GRAM = 50_000

/** Kiện hàng người bán khai lúc đặt đơn: cân cả kiện + kích thước thùng. Ô trống = null. */
export interface DeclaredParcel {
  weightGram?: number | null
  lengthMm?: number | null
  widthMm?: number | null
  heightMm?: number | null
}

/** Kiện ước tính từ sản phẩm: Σ cân, Σ cân quy đổi, kích thước khi đơn đúng 1 sản phẩm × 1 cái. */
export interface ProductParcel {
  actualGram: number
  volumetricGram: number
  dims: ParcelDims | null
}

/**
 * Kiện gửi hãng + cân tính cước của đơn. Cân / kích thước người bán khai thì dùng đúng số khai
 * (đơn nhiều món đóng chung một thùng); ô trống thì lấy theo sản phẩm. Quy đổi tắt → kiện không có
 * kích thước (không tính, không gửi hãng), cân tính cước = cân thực.
 */
export function resolveParcel(
  products: ProductParcel,
  declared?: DeclaredParcel | null,
  rule: VolumetricRule = DEFAULT_VOLUMETRIC_RULE,
): {
  weightGram: number
  dims: ParcelDims | null
  volumetricGram: number
  chargeableGram: number
} {
  const w = declared?.weightGram && declared.weightGram > 0 ? declared.weightGram : products.actualGram
  const weightGram = Math.max(MIN_PARCEL_GRAM, Math.round(w))
  const mm = [declared?.lengthMm, declared?.widthMm, declared?.heightMm].map((v) => (v && v > 0 ? Math.max(1, Math.round(v)) : 0))
  if (!rule.enabled) return { weightGram, dims: null, volumetricGram: 0, chargeableGram: weightGram }
  const dims = mm.every((v) => v > 0) ? { lengthMm: mm[0]!, widthMm: mm[1]!, heightMm: mm[2]! } : products.dims
  const volumetricGram = !rule.enabled ? 0 : dims ? volumetricWeightGram(dims, rule) : products.volumetricGram
  return { weightGram, dims, volumetricGram, chargeableGram: chargeableFromParts(weightGram, volumetricGram, rule) }
}

// ─── So sánh hai bảng ────────────────────────────────────────────

/** Một cân nặng rơi đúng vào hàng `i`: hàng "dưới X" → X − 1g (tại đúng X là hàng khác). */
function rowWeightGram(grid: PriceGrid, i: number): number {
  const g = toGram(grid.breakpointsKg[i]!)
  return kindAt(grid, i) === 'lt' ? Math.max(1, g - 1) : g
}

/**
 * Mọi điểm tính giá phân biệt của một vùng: từng mốc, cộng vùng bước tại 1 bước và
 * tại cân tối đa (chênh lệch trong vùng bước là hàm bậc nhất theo số bước, nên
 * cực trị nằm ở hai đầu).
 */
function chargePoints(grid: PriceGrid): { cell: number | 'step'; weightGram: number }[] {
  const pts: { cell: number | 'step'; weightGram: number }[] = grid.breakpointsKg.map((kg, i) => ({
    cell: i,
    weightGram: rowWeightGram(grid, i),
  }))
  const last = toGram(grid.breakpointsKg[grid.breakpointsKg.length - 1]!)
  const max = toGram(grid.maxWeightKg)
  if (max > last) {
    pts.push({ cell: 'step', weightGram: Math.min(last + toGram(grid.stepKg), max) })
    pts.push({ cell: 'step', weightGram: max })
  }
  return pts
}

export interface BelowCell extends CellRef {
  child: number
  parent: number
}

/**
 * P4: các ô mà bảng `child` thấp hơn bảng `parent` (giá nhập của người lập).
 * So cả từng mốc lẫn giá mỗi bước trên mốc cuối.
 */
export function compareBelow(grid: PriceGrid, child: PriceListData, parent: PriceListData): BelowCell[] {
  const out: BelowCell[] = []
  for (const zone of SHIPPING_ROUTES) {
    grid.breakpointsKg.forEach((kg, i) => {
      const c = child[zone].prices[i]!
      const p = parent[zone].prices[i]!
      if (c < p) out.push({ zone, cell: i, weightGram: toGram(kg), label: cellLabel(grid, zone, i, toGram(kg)), child: c, parent: p })
    })
    if (child[zone].stepPrice < parent[zone].stepPrice) {
      const w = chargePoints(grid).find((pt) => pt.cell === 'step')?.weightGram ?? toGram(grid.maxWeightKg)
      out.push({
        zone,
        cell: 'step',
        weightGram: w,
        label: `${ROUTE_LABELS[zone]}, mỗi ${fmtKg(toGram(grid.stepKg))} trên ${fmtKg(toGram(grid.breakpointsKg[grid.breakpointsKg.length - 1]!))}`,
        child: child[zone].stepPrice,
        parent: parent[zone].stepPrice,
      })
    }
  }
  return out
}

export interface MinMargin {
  margin: number
  at: CellRef
}

/**
 * Chênh lệch nhỏ nhất `sell − buy` trên mọi cân nặng tính được (D25: trần hoa hồng CTV
 * = chênh lệch nhỏ nhất giữa giá bán cho CTV và giá nhập của bên trả).
 */
export function minMargin(grid: PriceGrid, sell: PriceListData, buy: PriceListData): MinMargin {
  let best: MinMargin | null = null
  for (const zone of SHIPPING_ROUTES) {
    for (const pt of chargePoints(grid)) {
      // Hàng: so đúng giá của hàng đó — tra theo cân thì hàng "dưới X" bị hàng "đúng X" che mất.
      const m = pt.cell === 'step'
        ? priceFor(grid, sell, zone, pt.weightGram) - priceFor(grid, buy, zone, pt.weightGram)
        : sell[zone].prices[pt.cell]! - buy[zone].prices[pt.cell]!
      if (best === null || m < best.margin) {
        best = { margin: m, at: { zone, cell: pt.cell, weightGram: pt.weightGram, label: cellLabel(grid, zone, pt.cell, pt.weightGram) } }
      }
    }
  }
  return best!
}

/**
 * Chuyển một bảng sang khung mới (admin đổi mốc cân): giá tại mỗi mốc mới = giá bảng cũ
 * cho đúng cân đó (bậc cũ chứa nó, hoặc theo bước nếu trên mốc cuối cũ); giá bước quy
 * đổi theo tỷ lệ độ dài bước. Chủ bảng cần xem lại sau khi chuyển.
 */
export function convertPriceListData(from: PriceGrid, data: PriceListData, to: PriceGrid): PriceListData {
  const wide: PriceGrid = { ...from, maxWeightKg: Math.max(from.maxWeightKg, to.maxWeightKg, ...to.breakpointsKg) }
  const out = {} as PriceListData
  for (const zone of SHIPPING_ROUTES) {
    out[zone] = {
      prices: to.breakpointsKg.map((kg) => priceFor(wide, data, zone, toGram(kg))),
      stepPrice: Math.round((data[zone].stepPrice * to.stepKg) / from.stepKg),
    }
  }
  return out
}

// ─── So hai bảng có hàng cân khác nhau ───────────────────────────

/**
 * Khung ghép của hai bảng: mọi mốc của cả hai (mốc nào một bên tách "dưới X" / "đúng X"
 * thì khung ghép cũng tách), cộng các mốc trong vùng tính theo bước của từng bảng, tới cân
 * tối đa chung (nhỏ hơn của hai bảng). Trên khung này giá mỗi bảng là hằng số trong từng ô,
 * nên so từng ô là đúng tuyệt đối.
 */
export function unionGrid(a: PriceGrid, b: PriceGrid): PriceGrid {
  const max = Math.min(toGram(a.maxWeightKg), toGram(b.maxWeightKg))
  const points = new Map<number, boolean>() // gram → có tách điểm (lt/eq) không
  const add = (g: number, split: boolean) => {
    if (g > 0 && g <= max) points.set(g, (points.get(g) ?? false) || split)
  }
  for (const grid of [a, b]) {
    grid.breakpointsKg.forEach((kg, i) => add(toGram(kg), kindAt(grid, i) !== 'le'))
    const last = toGram(grid.breakpointsKg[grid.breakpointsKg.length - 1]!)
    const step = toGram(grid.stepKg)
    for (let g = last + step; g < max + step; g += step) add(Math.min(g, max), false)
  }
  add(max, false)
  const sorted = [...points.entries()].sort((x, y) => x[0] - y[0])
  const breakpointsKg: number[] = []
  const kinds: RowKind[] = []
  for (const [g, split] of sorted) {
    if (split) {
      breakpointsKg.push(g / 1000, g / 1000)
      kinds.push('lt', 'eq')
    } else {
      breakpointsKg.push(g / 1000)
      kinds.push('le')
    }
  }
  return { version: 0, breakpointsKg, kinds, stepKg: a.stepKg, maxWeightKg: max / 1000 }
}

/** Giá của một bảng trên từng ô của khung `g` (khung ghép — mỗi ô bảng có đúng một giá). */
export function onGrid(t: PriceTable, g: PriceGrid): PriceListData {
  const out = {} as PriceListData
  const reps = g.breakpointsKg.map((_, i) => rowWeightGram(g, i))
  for (const zone of SHIPPING_ROUTES) {
    out[zone] = { prices: reps.map((w) => priceFor(t.grid, t.data, zone, w)), stepPrice: 0 }
  }
  return out
}

/** P4 cho hai bảng bất kỳ: các ô bảng `child` thấp hơn bảng `parent`. */
export function compareTables(child: PriceTable, parent: PriceTable): BelowCell[] {
  const g = unionGrid(child.grid, parent.grid)
  return compareBelow(g, onGrid(child, g), onGrid(parent, g)).filter((c) => c.cell !== 'step')
}

/** D25 cho hai bảng bất kỳ: chênh lệch nhỏ nhất `sell − buy`. */
export function minMarginTables(sell: PriceTable, buy: PriceTable): MinMargin {
  const g = unionGrid(sell.grid, buy.grid)
  return minMargin(g, onGrid(sell, g), onGrid(buy, g))
}

/** Cùng hàng cân (mốc + loại + bước + cân tối đa) — để biết hai bảng có so ô-đối-ô trực tiếp được không. */
export function sameRows(a: PriceGrid, b: PriceGrid): boolean {
  return (
    a.breakpointsKg.length === b.breakpointsKg.length &&
    a.breakpointsKg.every((kg, i) => toGram(kg) === toGram(b.breakpointsKg[i]!) && kindAt(a, i) === kindAt(b, i)) &&
    toGram(a.stepKg) === toGram(b.stepKg) &&
    toGram(a.maxWeightKg) === toGram(b.maxWeightKg)
  )
}

